import PropTypes from 'prop-types';
import React from 'react';
import styled, { withTheme } from 'styled-components';

import NoSearchResultsWidget from '@ui/SearchResults/NoSearchResultsWidget.tsx';
import SearchResultsList from '@ui/SearchResults/SearchResultsList.tsx';
import SearchSuggestionsList from '@ui/SearchResults/SearchSuggestionsList.tsx';
import { rgba } from '@utils/dom.ts';

import { SEARCH_RESULTS_CONTAINER_ID } from './constants.ts';
import type { PoiOpenStatusResult, SearchPoi, TranslateFn } from './types.ts';

type WrapperProps = {
  $hasBottomPadding?: boolean;
};

type SearchResultsTitleProps = {
  $themeColors: Record<string, string>;
};

type SearchResultsProps = {
  className?: string;
  currentInputIndex?: number;
  defaultSearch?: SearchPoi[];
  hasBottomPadding?: boolean;
  isDesktop?: boolean;
  locations?: SearchPoi[];
  /** Open/closed state per POI id, resolved on demand by the plugin layer. */
  openStatuses?: Record<string, PoiOpenStatusResult>;
  onChooseMapLocationClicked?: () => void;
  onPoiClicked?: (poi: SearchPoi) => void;
  onSuggestionClicked?: (suggestion: string) => void;
  placesNearby?: SearchPoi[];
  placesNearbyTitle?: string;
  placesTitle?: string;
  searchSuggestions?: string[];
  handleEscapeKey?: () => void;
  T: TranslateFn;
  theme: { colors: Record<string, string> };
};

const Wrapper = styled.div<WrapperProps>`
  padding-bottom: ${({ $hasBottomPadding }) => ($hasBottomPadding ? '40px' : '0')};
  position: relative;
`;

const SearchResultsTitle = styled.li<SearchResultsTitleProps>`
  padding-block: 4px;
  padding-inline: 62px 14px;
  position: relative;
  margin: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between; // Added to space title and count
  background-color: ${({ $themeColors }) => rgba($themeColors.primary, 0.05)};
  border-top: 1px solid #ebebeb;
  border-bottom: 1px solid #ebebeb;
  font-weight: 500;
  font-size: 14px;
`;
const StyledUL = styled.ul`
  padding: 0;
  margin: 0;
  list-style: none;
`;

const StyledResultsCount = styled.span`
  font-size: 13px;
  font-weight: 200;
  white-space: nowrap;
`;

const SearchResults = withTheme(
  ({
    className,
    isDesktop = true,
    defaultSearch = [],
    searchSuggestions,
    placesNearby,
    locations,
    openStatuses,
    onSuggestionClicked = () => {},
    onChooseMapLocationClicked = () => {},
    onPoiClicked = () => {},
    placesNearbyTitle = '',
    placesTitle,
    currentInputIndex,
    theme,
    hasBottomPadding,
    handleEscapeKey = () => {},
    T,
  }: SearchResultsProps) => {
    const id = currentInputIndex != null ? `Stop${currentInputIndex}SuggestedLocations` : 'SuggestedLocations';
    const DefaultSearch = () => (
      <StyledUL role="group" aria-label={T('homeView:Suggestions')} id={id}>
        <SearchResultsList
          className={className}
          isDefault
          results={defaultSearch}
          onResultClicked={onPoiClicked}
          onChooseMapLocationClicked={onChooseMapLocationClicked}
          isDesktop={isDesktop}
          handleEscapeKey={handleEscapeKey}
          T={T}
        />
      </StyledUL>
    );

    const Suggestions = () => {
      if (!searchSuggestions || !searchSuggestions.length) {
        return null;
      }
      return (
        <SearchSuggestionsList
          suggestions={searchSuggestions}
          onSuggestionClicked={onSuggestionClicked}
          handleEscapeKey={handleEscapeKey}
          T={T}
        />
      );
    };

    const SuggestedLocations = (title: string | undefined, pois: SearchPoi[] | undefined) => {
      if (!pois || !pois.length) {
        return null;
      }
      return (
        <StyledUL
          role="group"
          aria-label={(title || 'Suggested locations') + ` ${T('ui:_count_ results', { count: pois.length })}`}
          id={id}
        >
          {!!title && (
            <SearchResultsTitle $themeColors={theme.colors} role="none">
              <span>{title}</span>
              <StyledResultsCount aria-hidden={true} data-testid="searchResultsCount">
                {T('ui:_count_ results', { count: pois.length })}
              </StyledResultsCount>
            </SearchResultsTitle>
          )}
          <SearchResultsList
            results={pois}
            onResultClicked={onPoiClicked}
            handleEscapeKey={handleEscapeKey}
            openStatuses={openStatuses}
            T={T}
          />
        </StyledUL>
      );
    };

    const Nearby = () => SuggestedLocations(placesNearbyTitle, placesNearby);
    const Locations = () => SuggestedLocations(placesTitle, locations);

    if (!searchSuggestions && !placesNearby && !locations && defaultSearch) {
      return (
        <div data-testid="DefaultSearchResults">
          <DefaultSearch />
        </div>
      );
    }

    if (
      (!searchSuggestions || !searchSuggestions.length) &&
      (!placesNearby || !placesNearby.length) &&
      (!locations || !locations.length)
    ) {
      return <NoSearchResultsWidget isDesktop={isDesktop} T={T} />;
    }

    return (
      <Wrapper
        data-testid="SearchResults"
        id={SEARCH_RESULTS_CONTAINER_ID}
        $hasBottomPadding={hasBottomPadding}
        className={className}
      >
        <Suggestions />
        <Nearby />
        <Locations />
      </Wrapper>
    );
  },
);

SearchResults.propTypes = {
  isDesktop: PropTypes.bool,
  defaultSearch: PropTypes.array,
  searchSuggestions: PropTypes.array,
  placesNearby: PropTypes.array,
  placesNearbyTitle: PropTypes.string,
  locations: PropTypes.array,
  onSuggestionClicked: PropTypes.func,
  onChooseMapLocationClicked: PropTypes.func,
  onPoiClicked: PropTypes.func,
  handleEscapeKey: PropTypes.func,
};

export default SearchResults;
